import { useEffect, useState } from 'react';

import { getLocalStorageReferrer, setLocalStorageItems } from 'src/utils/getLocalStorageReferrer';

interface NavigationState {
  currentPath: string;
  referrerPath: string | null;
  navigationHistory: string[];
}

const isWindow = typeof window !== 'undefined';

const LOCAL_STORAGE_SIZE_LIMIT = -10;

export const useReferrer = (): {
  navigationState: NavigationState;
  referrerPath: string | null;
  currentPath: string;
  isInitialized: boolean;
} => {
  const [navigationState, setNavigationState] = useState<NavigationState>({
    currentPath: isWindow ? window.location.pathname : '',
    referrerPath: null,
    navigationHistory: [],
  });
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!isWindow) {
      setIsInitialized(true);

      return;
    }

    const storedPaths = getLocalStorageReferrer();
    const currentPath = window.location.pathname;

    let referrerPath: string | null = null;

    if (storedPaths.length > 0) {
      referrerPath = storedPaths[storedPaths.length - 1];
    }

    const updatedPaths = [...storedPaths, currentPath];
    const trimmedPaths = updatedPaths.slice(LOCAL_STORAGE_SIZE_LIMIT);

    setLocalStorageItems(trimmedPaths);

    setNavigationState({
      currentPath,
      referrerPath,
      navigationHistory: trimmedPaths,
    });

    setIsInitialized(true);

    const handleLocationChange = (): void => {
      const newCurrentPath = window.location.pathname;
      const storedPaths = getLocalStorageReferrer();

      setNavigationState((prevState) => {
        // Only update if the path actually changed and we're not in the initial load
        if (newCurrentPath !== prevState.currentPath && isInitialized) {
          const newReferrerPath = prevState.currentPath;

          const updatedPaths = [...storedPaths, newCurrentPath];
          const trimmedPaths = updatedPaths.slice(LOCAL_STORAGE_SIZE_LIMIT);

          setLocalStorageItems(trimmedPaths);

          return {
            currentPath: newCurrentPath,
            referrerPath: newReferrerPath,
            navigationHistory: trimmedPaths,
          };
        }

        return prevState;
      });
    };

    const addEventListeners = (): (() => void) => {
      window.addEventListener('popstate', handleLocationChange);

      const originalPushState = history.pushState;
      const originalReplaceState = history.replaceState;

      history.pushState = (...args): void => {
        originalPushState.apply(history, args);
        handleLocationChange();
      };

      history.replaceState = (...args): void => {
        originalReplaceState.apply(history, args);
        handleLocationChange();
      };

      return (): void => {
        window.removeEventListener('popstate', handleLocationChange);
        history.pushState = originalPushState;
        history.replaceState = originalReplaceState;
      };
    };

    const cleanup = addEventListeners();

    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    navigationState,
    referrerPath: isInitialized ? navigationState.referrerPath : null,
    currentPath: navigationState.currentPath,
    isInitialized,
  };
};
